// Head-offset compatibility for the stem-selection ViewModel: which head offsets are
// selectable for a given stem system and head size. No API access (see stemServices.ts)
// and no Vue reactivity.
import type { CatalogHead, HeadSizeString } from '@/domain/catalog/head';
import {
    CocrOffset,
    displayableCocrOffsets,
    displayableType1Offsets,
    HeadSystem,
} from '@/domain/catalog/head';
import { headSystemForStem, type StemSystem } from '@/domain/catalog/stem';
import { isMinus6HeadOffsetAllowedForCup } from '@/domain/catalog/cup';
import { formatNumberSign } from '@/util/format/formatNumberSign';
import type { SelectableHeadOffset } from '../stem.types';

export type SelectableHeadOffsetsQuery = {
    heads: Map<string, CatalogHead>;
    stemSystem: StemSystem;
    headSize: HeadSizeString;
    selectedCupSize: number | null;
    dualMobility: boolean;
};

/** The selectable head offsets for a given stem system and head size. */
export const getSelectableHeadOffsetsForSystem = ({
    heads,
    stemSystem,
    headSize,
    selectedCupSize,
    dualMobility,
}: SelectableHeadOffsetsQuery): SelectableHeadOffset[] => {
    const isCompatibleWithTaperlocSystem = (head: CatalogHead): boolean =>
        displayableType1Offsets.includes(head.offset) &&
        head.size === headSize &&
        head.system === HeadSystem.TYPE1_COCR;

    const isCompatibleWithAvenirSystem = (head: CatalogHead): boolean => {
        if (
            head.offset === CocrOffset.MINUS_6 &&
            !isMinus6HeadOffsetAllowedForCup(selectedCupSize, dualMobility)
        ) {
            return false;
        }
        return (
            displayableCocrOffsets.includes(head.offset) &&
            head.size === headSize &&
            head.system === HeadSystem.COCR_1214
        );
    };

    const headSystem = headSystemForStem(stemSystem);
    const isHeadCompatible = (head: CatalogHead): boolean =>
        headSystem === HeadSystem.TYPE1_COCR
            ? isCompatibleWithTaperlocSystem(head)
            : isCompatibleWithAvenirSystem(head);

    return Array.from(heads.values())
        .filter(isHeadCompatible)
        .sort((a, b) => a.offset - b.offset)
        .map((head) => ({
            text: formatNumberSign(head.offset),
            headUrl: head.self,
        }));
};